import { useState } from 'react';
import { Calendar, Clock, MapPin, User, Phone, Mail, Users, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    pickupLocation: '',
    dropoffLocation: '',
    date: '',
    time: '',
    passengers: '1',
    notes: '',
    acceptedTerms: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.service || 
        !formData.pickupLocation || !formData.date || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.acceptedTerms) {
      toast.error('Please accept the terms and conditions to proceed');
      return;
    }

    // Mock booking confirmation
    const bookingId = 'RR-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    
    toast.success(`Booking confirmed! Your booking ID is ${bookingId}. We'll contact you shortly.`);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      pickupLocation: '',
      dropoffLocation: '',
      date: '',
      time: '',
      passengers: '1',
      notes: '',
      acceptedTerms: false
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative h-[300px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1707616824227-0f31ee435e73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxiYWhhbWFzJTIwYmVhY2h8ZW58MHx8fHwxNzc4Mzk4ODIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Book Your Ride</h1>
          <p className="text-xl">Quick and easy booking in just a few steps</p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-xl border-t-4 border-red-600">
              <CardHeader>
                <CardTitle className="text-3xl text-center">Complete Your Booking</CardTitle>
                <p className="text-center text-gray-600 mt-2">
                  Fill in the details below and we'll confirm your reservation shortly
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      <User className="w-5 h-5 text-red-600" />
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1242-525-8675"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Service Selection */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-red-600" />
                      Service Details
                    </h3>
                    <div>
                      <Label htmlFor="service">Select Service *</Label>
                      <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="airport-transfer">Airport Transfer</SelectItem>
                          <SelectItem value="island-tour">Island Tour</SelectItem>
                          <SelectItem value="corporate">Corporate Transportation</SelectItem>
                          <SelectItem value="special-event">Special Event</SelectItem>
                          <SelectItem value="hourly">Hourly Service</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pickupLocation">Pickup Location *</Label>
                        <Input
                          id="pickupLocation"
                          placeholder="e.g., Nassau Airport"
                          value={formData.pickupLocation}
                          onChange={(e) => handleChange('pickupLocation', e.target.value)}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dropoffLocation">Drop-off Location</Label>
                        <Input
                          id="dropoffLocation"
                          placeholder="e.g., Atlantis Resort"
                          value={formData.dropoffLocation}
                          onChange={(e) => handleChange('dropoffLocation', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-red-600" />
                      Date & Time
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="date">Date *</Label>
                        <Input
                          id="date"
                          type="date"
                          value={formData.date}
                          onChange={(e) => handleChange('date', e.target.value)}
                          required
                          className="mt-1"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <Label htmlFor="time">Pickup Time *</Label>
                        <Select value={formData.time} onValueChange={(value) => handleChange('time', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="06:00">6:00 AM</SelectItem>
                            <SelectItem value="06:30">6:30 AM</SelectItem>
                            <SelectItem value="07:00">7:00 AM</SelectItem>
                            <SelectItem value="07:30">7:30 AM</SelectItem>
                            <SelectItem value="08:00">8:00 AM</SelectItem>
                            <SelectItem value="08:30">8:30 AM</SelectItem>
                            <SelectItem value="09:00">9:00 AM</SelectItem>
                            <SelectItem value="09:30">9:30 AM</SelectItem>
                            <SelectItem value="10:00">10:00 AM</SelectItem>
                            <SelectItem value="10:30">10:30 AM</SelectItem>
                            <SelectItem value="11:00">11:00 AM</SelectItem>
                            <SelectItem value="11:30">11:30 AM</SelectItem>
                            <SelectItem value="12:00">12:00 PM</SelectItem>
                            <SelectItem value="12:30">12:30 PM</SelectItem>
                            <SelectItem value="13:00">1:00 PM</SelectItem>
                            <SelectItem value="13:30">1:30 PM</SelectItem>
                            <SelectItem value="14:00">2:00 PM</SelectItem>
                            <SelectItem value="14:30">2:30 PM</SelectItem>
                            <SelectItem value="15:00">3:00 PM</SelectItem>
                            <SelectItem value="15:30">3:30 PM</SelectItem>
                            <SelectItem value="16:00">4:00 PM</SelectItem>
                            <SelectItem value="16:30">4:30 PM</SelectItem>
                            <SelectItem value="17:00">5:00 PM</SelectItem>
                            <SelectItem value="17:30">5:30 PM</SelectItem>
                            <SelectItem value="18:00">6:00 PM</SelectItem>
                            <SelectItem value="18:30">6:30 PM</SelectItem>
                            <SelectItem value="19:00">7:00 PM</SelectItem>
                            <SelectItem value="19:30">7:30 PM</SelectItem>
                            <SelectItem value="20:00">8:00 PM</SelectItem>
                            <SelectItem value="20:30">8:30 PM</SelectItem>
                            <SelectItem value="21:00">9:00 PM</SelectItem>
                            <SelectItem value="21:30">9:30 PM</SelectItem>
                            <SelectItem value="22:00">10:00 PM</SelectItem>
                            <SelectItem value="22:30">10:30 PM</SelectItem>
                            <SelectItem value="23:00">11:00 PM</SelectItem>
                            <SelectItem value="23:30">11:30 PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="passengers">Passengers</Label>
                        <Select value={formData.passengers} onValueChange={(value) => handleChange('passengers', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 Passenger</SelectItem>
                            <SelectItem value="2">2 Passengers</SelectItem>
                            <SelectItem value="3">3 Passengers</SelectItem>
                            <SelectItem value="4">4 Passengers</SelectItem>
                            <SelectItem value="5">5+ Passengers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any special requests or flight details..."
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  {/* Terms and Conditions */}
                  <Card className="bg-amber-50 border-amber-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-4">
                        <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-900 mb-2">Important Booking Terms</h4>
                          <div className="space-y-2 text-sm text-amber-800">
                            <div className="flex items-start gap-2">
                              <span className="font-bold">•</span>
                              <p><strong>Late Penalty:</strong> If you are not ready at the scheduled pickup time, a late fee of <strong className="text-red-700">$5 USD per 10 minutes</strong> will be charged. Please be ready on time to avoid additional charges.</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="font-bold">•</span>
                              <p><strong>Cancellation Fee:</strong> Cancellations made less than 24 hours before scheduled pickup time will incur a <strong className="text-red-700">$25 USD cancellation fee</strong>.</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="font-bold">•</span>
                              <p><strong>Free Cancellation:</strong> Cancellations made 24 hours or more in advance are free of charge.</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="font-bold">•</span>
                              <p><strong>Payment:</strong> Payment is due at the time of service. We accept cash (USD/BSD) and major credit cards.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 mt-4 pt-4 border-t border-amber-300">
                        <Checkbox 
                          id="terms"
                          checked={formData.acceptedTerms}
                          onCheckedChange={(checked) => handleChange('acceptedTerms', checked)}
                        />
                        <label 
                          htmlFor="terms" 
                          className="text-sm text-amber-900 cursor-pointer"
                        >
                          I have read and agree to the booking terms, including the late penalty ($5/10min) and cancellation fee ($25 if cancelled within 24 hours).
                        </label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                    disabled={!formData.acceptedTerms}
                  >
                    Confirm Booking
                  </Button>

                  <p className="text-sm text-gray-600 text-center">
                    * Required fields. We'll contact you to confirm your booking and provide payment details.
                  </p>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="mt-8">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-center">Need Help?</h3>
                <div className="grid md:grid-cols-2 gap-4 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <Phone className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm text-gray-600">Call Us</p>
                      <a href="tel:+12425258675" className="font-semibold hover:text-red-600">
                        +1242-525-8675
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <Mail className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm text-gray-600">Email Us</p>
                      <a href="mailto:rasrio777@hotmail.com" className="font-semibold hover:text-red-600">
                        rasrio777@hotmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
